import React, { PureComponent } from 'react';
import Grid from '@material-ui/core/Grid';
import {
  TextField,
  FunctionField,
  SimpleShowLayout,
  BooleanField,
  DateField,
  showNotification,
  refreshView,
} from 'react-admin';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import { ContentCopy } from '@material-ui/icons';
import PropTypes from 'prop-types';
import ImgField from '../../../components/ImgField/ImgField';
import ListProgramsModal from './ListProgramsModal';
import roles from '../../../constants/roles';

const styles = {
  showLayout: {
    paddingTop: '0px !important',
    paddingBottom: '0px',
  },
};

class ShowGrid extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: null,
      modalOpened: false,
      copyingLoginLink: false,
      loginLinkDialog: false,
      generatedLoginLink: '',
    };
  }

  handleMenuOpen = (event) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleMenuClose = () => {
    this.setState({ anchorEl: null });
  };

  handleModalOpen = () => {
    this.setState({ modalOpened: true });
  };

  handleModalClose = () => {
    this.setState({ modalOpened: false });
  };

  handleGenerateLoginLink = async () => {
    const { record: { id: userId } = {} } = this.props;

    if (this.state.copyingLoginLink) return;

    this.setState({ copyingLoginLink: true });

    try {
      // Make direct API call to bypass dataProvider transformations
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      const directResponse = await fetch(`${apiUrl}/admin/users/${userId}/generate-login-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for authentication
      });

      if (!directResponse.ok) {
        throw new Error('Failed to generate login link');
      }

      const responseData = await directResponse.json();
      const loginUrl = responseData.loginUrl || '';

      this.setState({
        generatedLoginLink: loginUrl,
        loginLinkDialog: true,
        copyingLoginLink: false,
      });
      this.handleCopyToClipboard();
      this.handleMenuClose();
    } catch (error) {
      showNotification('Failed to generate login link', 'error');
      this.setState({ copyingLoginLink: false });
    }
  };

  handleCopyToClipboard = async () => {
    const { generatedLoginLink } = this.state;

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(generatedLoginLink);
        showNotification('Login link copied to clipboard!', 'info');
        return;
      }

      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = generatedLoginLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        showNotification('Login link copied to clipboard!', 'info');
      } else {
        showNotification('Copy failed - please copy manually', 'warning');
      }
    } catch (error) {
      showNotification('Copy failed - please copy manually', 'warning');
    }
  };

  handleCloseLinkDialog = () => {
    this.setState({
      loginLinkDialog: false,
      generatedLoginLink: '',
    });
  };

  render() {
    const { classes, history, permissions, ...props } = this.props;
    const { anchorEl } = this.state;
    return (
      <>
        <Grid container spacing={8}>
          <Grid item md={3} align="center">
            <ImgField
              {...props}
              source="picture"
              title="Picture"
              addLabel
              width="270"
              height="270"
            />
          </Grid>
          <Grid item md={8}>
            <SimpleShowLayout {...props} className={classes.showLayout}>
              <FunctionField
                source="Name"
                render={(record) => `${record.firstName} ${record.lastName}`}
              />
              <TextField source="email" />
              <DateField source="createdAt" label="Date Joined" />
              <BooleanField source="questionnaireComplete" label="Intro Questionairre Complete" />
              <BooleanField source="apAccess" label="AP Access" />
            </SimpleShowLayout>
          </Grid>
          <Grid item md={1}>
            {permissions === roles.superAdmin || permissions === roles.admin ? (
              <Button
                aria-owns={anchorEl ? 'simple-menu' : undefined}
                aria-haspopup="true"
                variant="outlined"
                onClick={this.handleMenuOpen}
              >
                Options
              </Button>
            ) : null}
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={this.handleMenuClose}
            >
              <MenuItem onClick={() => history.push(`/users/${this.props.record.id}`)}>
                Edit Customer
              </MenuItem>
              <MenuItem onClick={this.handleModalOpen}>Edit Program Access</MenuItem>
              <MenuItem
                onClick={this.handleGenerateLoginLink}
                disabled={this.state.copyingLoginLink}
              >
                {this.state.copyingLoginLink ? 'Generating...' : 'Generate Login Link'}
              </MenuItem>
            </Menu>
          </Grid>
        </Grid>
        <ListProgramsModal
          userId={this.props.record.id}
          opened={this.state.modalOpened}
          close={this.handleModalClose}
        />
        <Dialog
          open={this.state.loginLinkDialog}
          onClose={this.handleCloseLinkDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Customer Login Link</DialogTitle>
          <DialogContent>
            <Typography variant="body2" style={{ marginBottom: 16 }}>
              Use this link in an <b>incognito window</b> to login as customer. The link expires in
              15 minutes.
            </Typography>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ccc',
                borderRadius: 4,
                padding: 8,
                backgroundColor: '#f5f5f5',
              }}
            >
              <Typography
                variant="body2"
                style={{
                  flexGrow: 1,
                  wordBreak: 'break-all',
                  marginRight: 8,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                }}
              >
                {typeof this.state.generatedLoginLink === 'string'
                  ? this.state.generatedLoginLink
                  : 'No link generated'}
              </Typography>
              <IconButton
                onClick={this.handleCopyToClipboard}
                size="small"
                title="Copy to clipboard"
              >
                <ContentCopy />
              </IconButton>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseLinkDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

ShowGrid.propTypes = {
  history: PropTypes.objectOf(PropTypes.any).isRequired,
  permissions: PropTypes.string.isRequired,
};

export default withStyles(styles)(connect(null, { showNotification, refreshView })(ShowGrid));
